import { useState } from 'react';
import { useIsomorphicLayoutEffect } from 'utils/useIsomorphicLayoutEffect';

interface UseActiveSectionMinHeightParams {
  enabled: boolean;
}

const elementHeight = (selector: string) => document.querySelector(selector)?.getBoundingClientRect().height ?? 0;

// Once the tab bar is pinned (scrolled past the hero), the section fills the
// viewport space below header + tab bar, down to the footer. The hero is not
// part of this — by the time the section is visible, the hero has scrolled
// out of view, so its height must not be subtracted here (that would shrink
// short sections below what's needed to keep the page scrollable enough for
// the tab bar to ever reach its pinned position in the first place).
const computeMinHeight = () => {
  const chromeHeight = elementHeight('header') + elementHeight('#tabNavigation') + elementHeight('footer');
  return `calc(min(100dvh, 100vh) - ${chromeHeight}px)`;
};

// Always starts as undefined (matching what SSR renders, since there's no DOM to
// measure server-side) so the client's first hydration pass matches the server
// markup exactly. The layout effect below computes the real value synchronously,
// pre-paint, right after mount — a genuine state change that forces React to patch
// the DOM. Computing this eagerly in the initializer instead (skipping straight to
// the real value) silently breaks hydration: the mismatch is absorbed once and
// never corrected, since no later render ever touches this value again.
export function useActiveSectionMinHeight({ enabled }: UseActiveSectionMinHeightParams) {
  const [minHeight, setMinHeight] = useState<string | undefined>(undefined);

  useIsomorphicLayoutEffect(() => {
    if (!enabled) {
      setMinHeight(undefined);
      return undefined;
    }
    setMinHeight(computeMinHeight());
    const onResize = () => setMinHeight(computeMinHeight());
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [enabled]);

  return minHeight;
}
